/**
 * HelpTip — small "?" icon next to labels/fields/buttons that reveals a
 * tooltip from the help-content registry on hover or focus.
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section J rendering primitives.
 *
 * Usage:
 *   <Typography>Total Sent <HelpTip id="comm.overview.totalsent7d" /></Typography>
 */

import React from "react";
import { Tooltip, IconButton, Box } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useHelpContent } from "../../contexts/HelpContentContext";

export interface HelpTipProps {
  /** Tooltip id — looks up content.tooltips[id] in the help registry. */
  id: string;
  /** Tooltip placement relative to the icon. Default: "top". */
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end";
  /** Icon size in pixels. Default: 16. */
  size?: number;
  /** Inline-block sizing so it sits next to text. Default: true. */
  inline?: boolean;
}

export const HelpTip: React.FC<HelpTipProps> = ({
  id,
  placement = "top",
  size = 16,
  inline = true,
}) => {
  const { getTooltip } = useHelpContent();
  const entry = getTooltip(id);

  if (!entry) {
    // Dev mode already logged a warning in the context. In prod we silently
    // skip — never block the parent component.
    return null;
  }

  return (
    <Tooltip
      title={entry.copy}
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={100}
    >
      <Box
        component="span"
        sx={{
          display: inline ? "inline-flex" : "flex",
          alignItems: "center",
          verticalAlign: "middle",
          ml: 0.5,
        }}
        aria-label={`Help: ${entry.element}`}
      >
        <IconButton
          size="small"
          sx={{
            p: 0,
            color: "text.secondary",
            cursor: "help",
            "&:hover": { color: "primary.main" },
          }}
          tabIndex={0}
        >
          <HelpOutlineIcon sx={{ fontSize: size }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

export default HelpTip;
